import * as React from 'react'
import {
  TimeField as AriaTimeField,
  DateInput as AriaDateInput,
  type TimeValue,
} from 'react-aria-components'
import { cn } from '../../lib/utils'
import { DateSegment } from './date-field'

/**
 * TimeRangeField — a segmented start–end time input (e.g. business hours,
 * booking slots, availability windows). react-aria has no time-range field, so
 * this composes two `TimeField`s in one input box with an en-dash separator,
 * reusing the shared `DateSegment` styling. The keyboard-entry counterpart to a
 * (future) column-based `TimeRangePicker`.
 *
 *   <TimeRangeField value={range} onChange={setRange} />   // { start, end }
 *
 * `granularity="second"` adds seconds; `hourCycle={24}` switches to 24-hour.
 * Boolean props follow DS naming and apply to both fields.
 */

type FieldSize = 'sm' | 'md' | 'lg'
type FieldStatus = 'default' | 'error' | 'success'

export interface TimeRange {
  start: TimeValue | null
  end: TimeValue | null
}

const inputSize: Record<FieldSize, string> = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-9 px-3 text-sm',
  lg: 'h-10 px-3.5 text-base',
}

const boxBase =
  'inline-flex w-fit items-center gap-1.5 rounded-lg border bg-fbui-base-white text-fbui-gray-800 transition-colors focus-within:ring-2'

const boxStatus = {
  default:
    'border-fbui-gray-300 focus-within:border-fbui-basil-700 focus-within:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus-within:border-fbui-red-600 focus-within:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus-within:border-fbui-green-600 focus-within:ring-fbui-ring-success',
} as const

export interface TimeRangeFieldProps {
  value?: TimeRange | null
  defaultValue?: TimeRange | null
  onChange?: (value: TimeRange) => void
  hourCycle?: 12 | 24
  granularity?: 'hour' | 'minute' | 'second'
  size?: FieldSize
  status?: FieldStatus
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  invalid?: boolean
  /** Accessible name for the whole range group (announced by screen readers). */
  'aria-label'?: string
  /** Accessible name for the start field (default "Start time"). */
  startLabel?: string
  /** Accessible name for the end field (default "End time"). */
  endLabel?: string
  className?: string
}

export const TimeRangeField = React.forwardRef<
  HTMLDivElement,
  TimeRangeFieldProps
>(
  (
    {
      value,
      defaultValue,
      onChange,
      hourCycle,
      granularity,
      size = 'md',
      status,
      disabled,
      readOnly,
      required,
      invalid,
      'aria-label': ariaLabel,
      startLabel = 'Start time',
      endLabel = 'End time',
      className,
    },
    ref
  ) => {
    const resolved = invalid ? 'error' : (status ?? 'default')
    const isControlled = value !== undefined
    const [internal, setInternal] = React.useState<TimeRange>(
      defaultValue ?? { start: null, end: null }
    )
    const current = (isControlled ? value : internal) ?? {
      start: null,
      end: null,
    }

    const commit = (next: TimeRange) => {
      if (!isControlled) setInternal(next)
      onChange?.(next)
    }

    const fieldProps = {
      hourCycle,
      granularity,
      isDisabled: disabled,
      isReadOnly: readOnly,
      isRequired: required,
      isInvalid: invalid,
      className: 'flex items-center',
    } as const

    return (
      <div
        ref={ref}
        role="group"
        aria-label={ariaLabel}
        className={cn(
          boxBase,
          inputSize[size],
          boxStatus[resolved],
          disabled &&
            'cursor-not-allowed bg-fbui-gray-50 text-fbui-text-disabled',
          className
        )}
      >
        <AriaTimeField
          aria-label={startLabel}
          value={current.start}
          onChange={(t) => commit({ start: t, end: current.end })}
          {...fieldProps}
        >
          <AriaDateInput className="flex items-center">
            {(segment) => <DateSegment segment={segment} />}
          </AriaDateInput>
        </AriaTimeField>
        <span aria-hidden="true" className="text-fbui-gray-400">
          –
        </span>
        <AriaTimeField
          aria-label={endLabel}
          value={current.end}
          onChange={(t) => commit({ start: current.start, end: t })}
          {...fieldProps}
        >
          <AriaDateInput className="flex items-center">
            {(segment) => <DateSegment segment={segment} />}
          </AriaDateInput>
        </AriaTimeField>
      </div>
    )
  }
)
TimeRangeField.displayName = 'TimeRangeField'
